/**
 * Settings → Rules (RULES-ADMIN) — the pricing RULES that used to live in code:
 * which ZIPs count as "downtown" (the downtown diagnostic rate) and the shop's
 * business hours (which define the after-hours rate). Backed by org_setting, so
 * changing a rule never needs a developer. Read by useFeeContext / useBusinessHours.
 */
import { useState } from "react";
import { MapPin, Clock } from "lucide-react";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useOrgSettingJson, useSaveOrgSetting } from "@/lib/orgSettings";
import {
  DEFAULT_DOWNTOWN_ZIPS,
  DEFAULT_BUSINESS_HOURS,
  type BusinessHours,
} from "@/features/customer-360/serviceTypes";

export function RulesSection() {
  const canManage = useCan("settings.manage");
  return (
    <div className="space-y-4">
      <DowntownZipsCard canManage={canManage} />
      <BusinessHoursCard canManage={canManage} />
    </div>
  );
}

function DowntownZipsCard({ canManage }: { canManage: boolean }) {
  const { data: zips } = useOrgSettingJson<string[]>(
    "downtown_zips",
    DEFAULT_DOWNTOWN_ZIPS,
  );
  const save = useSaveOrgSetting();
  const stored = (zips ?? DEFAULT_DOWNTOWN_ZIPS).join(", ");
  const [draft, setDraft] = useState<string | null>(null);
  const current = draft ?? stored;
  const dirty = draft !== null && draft !== stored;
  const parsed = current
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div className="rounded-xl border border-hairline bg-white p-4 shadow-e1">
      <h4 className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
        <MapPin className="h-3.5 w-3.5 text-slate-400" /> Downtown ZIPs
        <InfoTip label="About downtown ZIPs">Jobs in these ZIPs get the downtown diagnostic rate.</InfoTip>
      </h4>
      <p className="mt-1 text-xs text-slate-500">Comma-separated.</p>
      <textarea
        value={current}
        disabled={!canManage}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        className="mt-2 w-full rounded-md border border-hairline px-2.5 py-2 text-sm text-slate-800 disabled:bg-slate-50"
      />
      <div className="mt-1.5 flex items-center justify-between">
        <span className="text-[11px] text-slate-400">{parsed.length} ZIPs</span>
        {canManage && dirty && (
          <button
            type="button"
            onClick={() =>
              save.mutate(
                { key: "downtown_zips", value: parsed },
                { onSuccess: () => setDraft(null) },
              )
            }
            disabled={save.isPending}
            className="rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        )}
      </div>
    </div>
  );
}

function BusinessHoursCard({ canManage }: { canManage: boolean }) {
  const { data: bh } = useOrgSettingJson<BusinessHours>(
    "business_hours",
    DEFAULT_BUSINESS_HOURS,
  );
  const cur = bh ?? DEFAULT_BUSINESS_HOURS;
  const save = useSaveOrgSetting();
  const [start, setStart] = useState<string | null>(null);
  const [end, setEnd] = useState<string | null>(null);
  const s = start ?? String(cur.start);
  const e = end ?? String(cur.end);
  const dirty =
    (start !== null && start !== String(cur.start)) ||
    (end !== null && end !== String(cur.end));
  const fmt = (h: number) =>
    `${((h + 11) % 12) + 1}${h < 12 ? "am" : "pm"}`;

  return (
    <div className="rounded-xl border border-hairline bg-white p-4 shadow-e1">
      <h4 className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
        <Clock className="h-3.5 w-3.5 text-slate-400" /> Business Hours
        <InfoTip label="About business hours">Calls outside these hours, and on weekends, get the after-hours rate.</InfoTip>
      </h4>
      <p className="mt-1 text-xs text-slate-500">
        24-hour clock ({fmt(Number(s))}–{fmt(Number(e))}).
      </p>
      <div className="mt-2 flex items-center gap-2 text-sm text-slate-700">
        <span className="text-xs text-slate-400">Open</span>
        <input
          type="number"
          min={0}
          max={23}
          value={s}
          disabled={!canManage}
          onChange={(ev) => setStart(ev.target.value)}
          className="w-16 rounded-md border border-hairline px-2 py-1 text-center text-sm disabled:bg-slate-50"
        />
        <span className="text-slate-400">to</span>
        <input
          type="number"
          min={0}
          max={23}
          value={e}
          disabled={!canManage}
          onChange={(ev) => setEnd(ev.target.value)}
          className="w-16 rounded-md border border-hairline px-2 py-1 text-center text-sm disabled:bg-slate-50"
        />
        <span className="text-xs text-slate-400">close</span>
        {canManage && dirty && (
          <button
            type="button"
            onClick={() =>
              save.mutate(
                {
                  key: "business_hours",
                  value: { start: Number(s), end: Number(e) },
                },
                {
                  onSuccess: () => {
                    setStart(null);
                    setEnd(null);
                  },
                },
              )
            }
            disabled={save.isPending}
            className="ml-auto rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        )}
      </div>
    </div>
  );
}
